import {
  DocumentPackageType,
  DocumentPackageTypeLabels,
  Language,
  ProcessorPreferenceKey,
  EmployerRole,
  RfpRegion,
  RfpRegionLabels,
  SiteDueDiligenceFacilityType,
} from '@consigli/types';

import { ConflictTypeSelector, Fieldset, Field, Select, TextInput } from '@/components';
import { FileUpload } from '@/components/file-upload';

import { BidReviewSection } from './bid-review-section';
import { type BidReviewBidEntry } from './logic/use-unified-service-form';
import { languageOptions } from './logic/utils';
import {
  SiteDueDiligenceFields,
  // TemplateMode,
} from './site-due-diligence-fields';
import { CommentsMode, PackageInputMode, VersionCompareSection } from './version-compare-section';

const employerRoleOptions: { label: string; value: EmployerRole }[] = [
  { label: 'Contractor', value: EmployerRole.CONTRACTOR },
  { label: 'Developer', value: EmployerRole.DEVELOPER },
  { label: 'Project Developer', value: EmployerRole.PROJECT_DEVELOPER },
  { label: 'Engineer', value: EmployerRole.ENGINEER },
];

interface DocumentPackageFormProps {
  projectId: number;
  name: string;
  packageType: DocumentPackageType;
  language: Language;
  files: File[];
  selectedConflictTypes: ProcessorPreferenceKey[];
  employerRole: EmployerRole | null;
  rfpRegion: RfpRegion | null;
  errors?: Record<string, string>;
  onNameChange: (value: string) => void;
  onPackageTypeChange: (value: DocumentPackageType) => void;
  onLanguageChange: (value: Language) => void;
  onFilesChange: (files: File[]) => void;
  onSelectedConflictTypesChange: (value: ProcessorPreferenceKey[]) => void;
  onEmployerRoleChange: (value: EmployerRole | null) => void;
  onRfpRegionChange: (value: RfpRegion | null) => void;
  // Bid Review (OFFER) specific props
  bidReviewBids: BidReviewBidEntry[];
  bidReviewServices: string[];
  bidReviewAutoFillLevelingSheet: boolean;
  bidReviewLevelingSheetFiles: File[];
  tenderPackageMode: 'existing' | 'new';
  existingTenderPackageId: string | null;
  tenderFiles: File[];
  onBidReviewBidsChange: (bids: BidReviewBidEntry[]) => void;
  onBidReviewServicesChange: (services: string[]) => void;
  onBidReviewAutoFillLevelingSheetChange: (value: boolean) => void;
  onBidReviewLevelingSheetFilesChange: (files: File[]) => void;
  onTenderPackageModeChange: (mode: 'existing' | 'new') => void;
  onExistingTenderPackageIdChange: (id: string | null) => void;
  onTenderFilesChange: (files: File[]) => void;
  // Version Compare specific props
  versionComparePrimaryMode: PackageInputMode;
  versionCompareSecondaryMode: PackageInputMode;
  versionComparePrimaryPackageId: string | null;
  versionCompareSecondaryPackageId: string | null;
  versionComparePrimaryFiles: File[];
  versionCompareSecondaryFiles: File[];
  versionCompareCommentsMode: CommentsMode;
  versionCompareCommentRegisterFiles: File[];
  onVersionComparePrimaryModeChange: (mode: PackageInputMode) => void;
  onVersionCompareSecondaryModeChange: (mode: PackageInputMode) => void;
  onVersionComparePrimaryPackageIdChange: (id: string | null) => void;
  onVersionCompareSecondaryPackageIdChange: (id: string | null) => void;
  onVersionComparePrimaryFilesChange: (files: File[]) => void;
  onVersionCompareSecondaryFilesChange: (files: File[]) => void;
  onVersionCompareCommentsModeChange: (mode: CommentsMode) => void;
  onVersionCompareCommentRegisterFilesChange: (files: File[]) => void;
  // Site Due Diligence specific props
  siteDueDiligenceFacilityType?: SiteDueDiligenceFacilityType | null;
  // templateMode?: TemplateMode;
  // customTemplateFile?: File | null;
  // selectedCustomer?: string | null;
  locationLat?: string;
  locationLon?: string;
  documentFiles?: File[];
  // gisFiles?: File[];
  // urls?: string[];
  // allowLLMForDocuments?: boolean;
  onSiteDueDiligenceFacilityTypeChange?: (value: SiteDueDiligenceFacilityType) => void;
  // onTemplateModeChange?: (value: TemplateMode) => void;
  // onCustomTemplateFileChange?: (file: File | null) => void;
  // onSelectedCustomerChange?: (value: string | null) => void;
  onLocationLatChange?: (value: string) => void;
  onLocationLonChange?: (value: string) => void;
  onDocumentFilesChange?: (files: File[]) => void;
  // onGisFilesChange?: (files: File[]) => void;
  // onUrlsChange?: (urls: string[]) => void;
  // onAllowLLMForDocumentsChange?: (value: boolean) => void;
}

/**
 * Form for creating a document package.
 * @param name - The name of the document package.
 * @param packageType - The type of the document package.
 * @param language - The language of the document package.
 * @param files - The files of the document package.
 * @param selectedConflictTypes - The selected conflict types for filtering findings.
 * @param errors - The errors of the document package.
 * @param onNameChange - The function to call when the name is changed.
 * @param onPackageTypeChange - The function to call when the package type is changed.
 * @param onLanguageChange - The function to call when the language is changed.
 * @param onFilesChange - The function to call when the files are changed.
 * @param onSelectedConflictTypesChange - The function to call when selected conflict types change.
 * @returns The document package form.
 */
export const DocumentPackageForm = ({
  projectId,
  name,
  packageType,
  language,
  files,
  selectedConflictTypes,
  employerRole,
  rfpRegion,
  errors = {},
  onNameChange,
  onPackageTypeChange,
  onLanguageChange,
  onFilesChange,
  onSelectedConflictTypesChange,
  onEmployerRoleChange,
  onRfpRegionChange,
  // Bid Review (OFFER) props
  bidReviewBids,
  bidReviewServices,
  bidReviewAutoFillLevelingSheet,
  bidReviewLevelingSheetFiles,
  tenderPackageMode,
  existingTenderPackageId,
  tenderFiles,
  onBidReviewBidsChange,
  onBidReviewServicesChange,
  onBidReviewAutoFillLevelingSheetChange,
  onBidReviewLevelingSheetFilesChange,
  onTenderPackageModeChange,
  onExistingTenderPackageIdChange,
  onTenderFilesChange,
  // Version Compare props
  versionComparePrimaryMode,
  versionCompareSecondaryMode,
  versionComparePrimaryPackageId,
  versionCompareSecondaryPackageId,
  versionComparePrimaryFiles,
  versionCompareSecondaryFiles,
  versionCompareCommentsMode,
  versionCompareCommentRegisterFiles,
  onVersionComparePrimaryModeChange,
  onVersionCompareSecondaryModeChange,
  onVersionComparePrimaryPackageIdChange,
  onVersionCompareSecondaryPackageIdChange,
  onVersionComparePrimaryFilesChange,
  onVersionCompareSecondaryFilesChange,
  onVersionCompareCommentsModeChange,
  onVersionCompareCommentRegisterFilesChange,
  // Site Due Diligence props
  siteDueDiligenceFacilityType = null,
  // templateMode = 'default',
  // customTemplateFile = null,
  // selectedCustomer = null,
  locationLat = '',
  locationLon = '',
  documentFiles = [],
  // gisFiles = [],
  // urls = [],
  // allowLLMForDocuments = false,
  onSiteDueDiligenceFacilityTypeChange,
  // onTemplateModeChange,
  // onCustomTemplateFileChange,
  // onSelectedCustomerChange,
  onLocationLatChange,
  onLocationLonChange,
  onDocumentFilesChange,
  // onGisFilesChange,
  // onUrlsChange,
  // onAllowLLMForDocumentsChange,
}: DocumentPackageFormProps) => {
  const isSiteDueDiligence = packageType === DocumentPackageType.SITE_DUE_DILIGENCE;
  const isRfpRfqReview = packageType === DocumentPackageType.RFP_RFQ_REVIEW;
  const isOffer = packageType === DocumentPackageType.OFFER;
  const isVersionCompare = packageType === DocumentPackageType.VERSION_COMPARE;

  const documentPackageService = Object.entries(DocumentPackageTypeLabels).map(
    ([value, label]) => ({
      label,
      value: value as DocumentPackageType,
    }),
  );

  const rfpRegionOptions = Object.entries(RfpRegionLabels).map(([value, label]) => ({
    label,
    value: value as RfpRegion,
  }));

  return (
    <Fieldset legend="Insight Package">
      <p className="text-sm text-gray-500">
        Please fill in the details to add a new insight package to the project
      </p>
      <Field label="Name" htmlFor="name-input" required error={errors.name}>
        <TextInput
          placeholder="Please enter a package name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full"
        />
      </Field>
      <Field label="Package Type" htmlFor="package-type-select" required error={errors.packageType}>
        <Select
          options={documentPackageService}
          placeholder="Select Package Type"
          value={documentPackageService.find((opt) => opt.value === packageType)}
          onValueChange={(option) => onPackageTypeChange(option.value)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          className="w-full"
        />
      </Field>
      <Field
        label="Who are you working for"
        htmlFor="working-for-role-select"
        error={errors.employerRole}
      >
        <Select
          options={employerRoleOptions}
          placeholder="Select role"
          value={
            employerRole ? employerRoleOptions.find((opt) => opt.value === employerRole) : undefined
          }
          onValueChange={(option) => onEmployerRoleChange(option.value)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          className="w-full"
        />
      </Field>
      {isRfpRfqReview && (
        <Field label="Region/CM" htmlFor="rfp-region-select" error={errors.rfpRegion}>
          <Select
            options={rfpRegionOptions}
            placeholder="Select AECOM Approval Matrix Region/CM"
            value={rfpRegion ? rfpRegionOptions.find((opt) => opt.value === rfpRegion) : undefined}
            onValueChange={(option) => onRfpRegionChange(option.value)}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            className="w-full"
          />
        </Field>
      )}
      <Field label="Select Language" htmlFor="language-select" required error={errors.language}>
        <Select
          options={languageOptions}
          placeholder="Select Language"
          value={languageOptions.find((opt) => opt.value === language)}
          onValueChange={(option) => onLanguageChange(option.value)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          className="w-full"
        />
      </Field>
      {packageType === DocumentPackageType.TENDER_DOCUMENTS && (
        <ConflictTypeSelector
          selectedConflictTypes={selectedConflictTypes}
          onSelectionChange={onSelectedConflictTypesChange}
        />
      )}
      {/* Standard files upload - hidden for Site Due Diligence, Version Compare, and Bid Review */}
      {!isSiteDueDiligence && !isVersionCompare && !isOffer && (
        <Field label="Files" htmlFor="files-input">
          <FileUpload setFiles={onFilesChange} placeholder="Upload files" files={files} />
        </Field>
      )}
      {/* Bid Review (OFFER) specific fields */}
      {isOffer && (
        <BidReviewSection
          projectId={projectId}
          bids={bidReviewBids}
          services={bidReviewServices}
          autoFillLevelingSheet={bidReviewAutoFillLevelingSheet}
          levelingSheetFiles={bidReviewLevelingSheetFiles}
          tenderMode={tenderPackageMode}
          existingTenderPackageId={existingTenderPackageId}
          tenderFiles={tenderFiles}
          selectedConflictTypes={selectedConflictTypes}
          errors={errors}
          onBidsChange={onBidReviewBidsChange}
          onServicesChange={onBidReviewServicesChange}
          onAutoFillLevelingSheetChange={onBidReviewAutoFillLevelingSheetChange}
          onLevelingSheetFilesChange={onBidReviewLevelingSheetFilesChange}
          onTenderModeChange={onTenderPackageModeChange}
          onExistingTenderPackageIdChange={onExistingTenderPackageIdChange}
          onTenderFilesChange={onTenderFilesChange}
          onSelectedConflictTypesChange={onSelectedConflictTypesChange}
        />
      )}
      {/* Version Compare specific fields */}
      {isVersionCompare && (
        <VersionCompareSection
          projectId={projectId}
          primaryMode={versionComparePrimaryMode}
          secondaryMode={versionCompareSecondaryMode}
          primaryPackageId={versionComparePrimaryPackageId}
          secondaryPackageId={versionCompareSecondaryPackageId}
          primaryFiles={versionComparePrimaryFiles}
          secondaryFiles={versionCompareSecondaryFiles}
          commentsMode={versionCompareCommentsMode}
          commentRegisterFiles={versionCompareCommentRegisterFiles}
          errors={errors}
          onPrimaryModeChange={onVersionComparePrimaryModeChange}
          onSecondaryModeChange={onVersionCompareSecondaryModeChange}
          onPrimaryPackageIdChange={onVersionComparePrimaryPackageIdChange}
          onSecondaryPackageIdChange={onVersionCompareSecondaryPackageIdChange}
          onPrimaryFilesChange={onVersionComparePrimaryFilesChange}
          onSecondaryFilesChange={onVersionCompareSecondaryFilesChange}
          onCommentsModeChange={onVersionCompareCommentsModeChange}
          onCommentRegisterFilesChange={onVersionCompareCommentRegisterFilesChange}
        />
      )}
      {/* Site Due Diligence specific fields */}
      {isSiteDueDiligence &&
        onSiteDueDiligenceFacilityTypeChange &&
        onLocationLatChange &&
        onLocationLonChange &&
        onDocumentFilesChange && (
          <SiteDueDiligenceFields
            facilityType={siteDueDiligenceFacilityType}
            // templateMode={templateMode}
            // customTemplateFile={customTemplateFile}
            // selectedCustomer={selectedCustomer}
            locationLat={locationLat}
            locationLon={locationLon}
            documentFiles={documentFiles}
            // gisFiles={gisFiles}
            // urls={urls}
            // allowLLMForDocuments={allowLLMForDocuments}
            errors={errors}
            onFacilityTypeChange={onSiteDueDiligenceFacilityTypeChange}
            // onTemplateModeChange={onTemplateModeChange}
            // onCustomTemplateFileChange={onCustomTemplateFileChange}
            // onSelectedCustomerChange={onSelectedCustomerChange}
            onLocationLatChange={onLocationLatChange}
            onLocationLonChange={onLocationLonChange}
            onDocumentFilesChange={onDocumentFilesChange}
            // onGisFilesChange={onGisFilesChange}
            // onUrlsChange={onUrlsChange}
            // onAllowLLMForDocumentsChange={onAllowLLMForDocumentsChange}
          />
        )}
    </Fieldset>
  );
};
